import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Button from 'common/components/button/Button';
import Modal, { ModalHeader, ModalBody, ModalFooter } from 'common/components/modal/Modal';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import BudgetsService from 'api/budgets/BudgetsService';
import { setLines } from 'store/budget/action';
import { BudgetConfigDto, BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { MaterialsListLabels } from 'api/budgets/models/ExportMaterialsListDto';
import { evaluateExecutionStudyHours } from './budgetConfigEvaluation';
import styles from './ExportMaterialsListModal.module.scss';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    budgetId?: string;
    budgetNumber?: string;
    budgetName?: string;
    lines: BudgetLineDto[];
    configs: BudgetConfigDto[];
}

const ExportMaterialsListModal: React.FC<Props> = ({ isOpen, onClose, budgetId, budgetNumber, budgetName, lines, configs }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const sections = useMemo(
        () => (lines ?? []).filter(l => l.lineType === BudgetLineType.SECTION && l.id),
        [lines]
    );

    // For each section, the ids of its ancestor SECTION lines (walking up by parentKey).
    // A section can only be an article if none of its ancestor sections is one.
    const ancestorSectionIds = useMemo(() => {
        const byKey = new Map<string, BudgetLineDto>();
        (lines ?? []).forEach(l => { if (l.key) byKey.set(l.key, l); });

        const result = new Map<string, string[]>();
        sections.forEach(section => {
            const ancestors: string[] = [];
            let current: BudgetLineDto | undefined = section;
            while (current?.parentKey) {
                const parent = byKey.get(current.parentKey);
                if (!parent) break;
                if (parent.lineType === BudgetLineType.SECTION && parent.id) {
                    ancestors.push(parent.id);
                }
                current = parent;
            }
            result.set(section.id as string, ancestors);
        });
        return result;
    }, [lines, sections]);

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const isBlocked = (id: string, selected: Set<string>) =>
        (ancestorSectionIds.get(id) ?? []).some(a => selected.has(a));

    const descendantSectionIds = (id: string) =>
        sections.filter(s => (ancestorSectionIds.get(s.id as string) ?? []).includes(id)).map(s => s.id as string);

    // Sibling sections share the same number prefix (the number minus its last segment), so they are
    // at the same level inside the same section by their numbering — e.g. 3.8.1-1 ↔ 3.8.1-2 ↔ 3.8.1-3,
    // but NOT 3.7.2 ↔ 3.8 (different number structures).
    const numberGroupKey = (num?: string | null) => {
        const n = (num ?? '').trim();
        if (!n) return null; // no number → treat as its own group (handled by caller)
        const idx = Math.max(n.lastIndexOf('.'), n.lastIndexOf('-'));
        return idx >= 0 ? n.substring(0, idx) : '';
    };

    const siblingSectionIds = (id: string) => {
        const target = sections.find(s => s.id === id);
        const key = numberGroupKey(target?.number);
        if (key === null) {
            return [id]; // unnumbered section: only itself
        }
        return sections.filter(s => numberGroupKey(s.number) === key).map(s => s.id as string);
    };

    useEffect(() => {
        if (!isOpen) return;
        // Keep only articles whose ancestors are not also articles (defensive against legacy data).
        const flagged = new Set(sections.filter(s => s.isArticle).map(s => s.id as string));
        const valid = new Set<string>();
        flagged.forEach(id => {
            if (!(ancestorSectionIds.get(id) ?? []).some(a => flagged.has(a))) {
                valid.add(id);
            }
        });
        setSelectedIds(valid);
    }, [isOpen, sections, ancestorSectionIds]);

    const toggle = (id: string) => {
        setSelectedIds(prev => {
            if (isBlocked(id, prev)) {
                return prev;
            }
            const next = new Set(prev);
            // Toggle the whole sibling group together (all sections at the same level in the section).
            const siblings = siblingSectionIds(id);
            if (next.has(id)) {
                siblings.forEach(sib => next.delete(sib));
            } else {
                siblings.forEach(sib => {
                    next.add(sib);
                    // Selecting a section blocks its descendant sections — deselect them.
                    descendantSectionIds(sib).forEach(d => next.delete(d));
                });
            }
            return next;
        });
    };

    const onExport = async () => {
        if (!budgetId || selectedIds.size === 0) return;

        const title = [budgetNumber, budgetName].filter(Boolean).join(' - ');
        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        const exportedAt = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

        const labels: MaterialsListLabels = {
            title,
            exportedAt,
            exportedAtLabel: t('budgets.budget.export_materials.exported_at_label'),
            sheetByArticle: t('budgets.budget.export_materials.sheet_by_article'),
            sheetTotals: t('budgets.budget.export_materials.sheet_totals'),
            article: t('budgets.budget.export_materials.col_article'),
            quantity: t('budgets.budget.export_materials.col_quantity'),
            unit: t('budgets.budget.export_materials.col_unit'),
            material: t('budgets.budget.export_materials.col_material'),
            unitPrice: t('budgets.budget.export_materials.col_unit_price'),
            total: t('budgets.budget.export_materials.col_total'),
            studies: t('budgets.budget.export_materials.col_studies'),
            fileName: t('budgets.budget.export_materials.file_name'),
        };

        try {
            Loading.show();
            const executionStudyHours = evaluateExecutionStudyHours(configs ?? []);
            const blob = await BudgetsService.exportMaterialsList({
                budgetId,
                articleLineIds: Array.from(selectedIds),
                executionStudyHours,
                labels,
            });
            const fileName = budgetNumber ? `${labels.fileName} - ${budgetNumber}` : labels.fileName;
            BudgetsService.downloadBlob(blob, `${fileName}.xlsx`);

            // Reflect the persisted selection in the store so a re-open comes pre-selected.
            const updatedLines = (lines ?? []).map(l =>
                l.lineType === BudgetLineType.SECTION && l.id
                    ? { ...l, isArticle: selectedIds.has(l.id) }
                    : l
            );
            dispatch(setLines(updatedLines));

            onClose();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t export the budget materials list', error);
            Toast.error(t('budgets.budget.export_materials.export_error'));
        } finally {
            Loading.hide();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={styles.modal}
            style={{ content: { maxWidth: '95vw', width: '1100px' } }}
        >
            <ModalHeader className={styles.header}>
                <h5 className={styles.title}>{t('budgets.budget.export_materials.modal_title')}</h5>
                <button type="button" className="btn-close" onClick={onClose} />
            </ModalHeader>

            <ModalBody className={styles.body}>
                <div className={styles.note}>{t('budgets.budget.export_materials.info_element_types')}</div>
                <div className={styles.note}>{t('budgets.budget.export_materials.info_parameters')}</div>
                <p className={styles.description}>{t('budgets.budget.export_materials.instruction')}</p>
                {selectedIds.size === 0 && (
                    <div className={styles.warning}>{t('budgets.budget.export_materials.select_warning')}</div>
                )}

                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr className={styles.headerRow}>
                                <th className={styles.checkCol}></th>
                                <th className={styles.numberCol}>{t('budgets.budget.export_materials.col_number')}</th>
                                <th>{t('budgets.budget.export_materials.col_designation')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sections.map(section => {
                                const id = section.id as string;
                                const checked = selectedIds.has(id);
                                const blocked = isBlocked(id, selectedIds);
                                return (
                                    <tr
                                        key={id}
                                        className={`${styles.dataRow} ${checked ? styles.selectedRow : ''} ${blocked ? styles.blockedRow : ''}`}
                                        onClick={() => !blocked && toggle(id)}
                                    >
                                        <td className={styles.checkCol}>
                                            <input
                                                type="checkbox"
                                                className={styles.checkInput}
                                                checked={checked}
                                                disabled={blocked}
                                                onChange={() => toggle(id)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td className={styles.numberCol} style={{ paddingLeft: `${0.5 + (section.level ?? 0) * 1}rem` }}>
                                            {section.number}
                                        </td>
                                        <td className={styles.designationCell}>{section.designation}</td>
                                    </tr>
                                );
                            })}
                            {sections.length === 0 && (
                                <tr>
                                    <td colSpan={3} className={styles.designationCell}>
                                        {t('budgets.budget.export_materials.no_sections')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </ModalBody>

            <ModalFooter className={styles.footer}>
                <Button variant="secondary" onClick={onClose}>
                    {t('common.cancel')}
                </Button>
                <div className={styles.footerSpacer} />
                <Button onClick={onExport} disabled={selectedIds.size === 0}>
                    {t('budgets.budget.export_materials.export')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ExportMaterialsListModal;
